import { useApiError, useTranslation } from "@/hooks";
import {
	DashboardTable,
	EmptyRow,
	LoadingRows,
	SectionCard,
} from "@/pages/dashboard/components";
import { useGetCommissionSummary } from "@/services";
import { formatNumber } from "@/utils/tools";
import {
	ErrorBoundary,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@st-marketplace/ui";

/** Per user: commission paid to the system, what is pending, what is owed. */
export function CommissionSummaryTable() {
	const { t } = useTranslation();
	const { getErrorInterface } = useApiError();
	const { data, isLoading, error, refetch } = useGetCommissionSummary();
	const rows = data ?? [];
	const totalPaid = rows.reduce((sum, row) => sum + Number(row.paid), 0);

	const head = [
		t("payment.user"),
		t("admin.payments.paid"),
		t("admin.payments.payments"),
		t("payment.pending"),
		t("admin.payments.currentDebt"),
		t("admin.payments.rate"),
	];

	return (
		<SectionCard
			title={t("admin.payments.commissionSummary")}
			metric={
				<span className="text-sm text-secondary-icon tabular-nums">
					{t("admin.payments.totalPaid", {
						amount: formatNumber(totalPaid, "price"),
					})}
				</span>
			}
		>
			<ErrorBoundary
				isLoading={false}
				error={error ? getErrorInterface(error) : undefined}
				onReset={refetch}
			>
				<DashboardTable>
					<TableHeader>
						<TableRow>
							{head.map((label) => (
								<TableHead key={label}>{label}</TableHead>
							))}
						</TableRow>
					</TableHeader>
					<TableBody>
						{isLoading ? (
							<LoadingRows columns={head.length} />
						) : rows.length === 0 ? (
							<EmptyRow colSpan={head.length}>
								{t("admin.payments.noCommission")}
							</EmptyRow>
						) : (
							rows.map((row) => (
								<TableRow key={row.user_id}>
									<TableCell data-label={head[0]}>{row.display_name}</TableCell>
									<TableCell data-label={head[1]} className="tabular-nums">
										{formatNumber(row.paid, "price")}
									</TableCell>
									<TableCell data-label={head[2]} className="tabular-nums">
										{row.payments}
									</TableCell>
									<TableCell data-label={head[3]} className="tabular-nums">
										{[
											Number(row.pending)
												? formatNumber(row.pending, "price")
												: null,
											Number(row.pending_usdt)
												? `${formatNumber(row.pending_usdt)} USDT`
												: null,
										]
											.filter(Boolean)
											.join(" + ") || "-"}
									</TableCell>
									<TableCell data-label={head[4]} className="tabular-nums">
										{formatNumber(row.current_debt, "price")}
									</TableCell>
									<TableCell data-label={head[5]} className="tabular-nums">
										{row.commission_rate === null
											? t("admin.payments.globalRate")
											: formatNumber(row.commission_rate)}
									</TableCell>
								</TableRow>
							))
						)}
					</TableBody>
				</DashboardTable>
			</ErrorBoundary>
		</SectionCard>
	);
}
